import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { accessToken } from '../../../../configuration/tokenHandling/accessToken';
import { getMarketplaceBackend } from '../../config/appConfig';
import { mapFreeTrialResponse } from './freeTrialMapper';
import type { FreeTrialState } from './types';

interface UpdateFreeTrialEligibilityRequest {
    status: 'eligible' | 'ineligible';
    reason: string;
}

interface ExtendFreeTrialRequest {
    ends_at: string;
    reason: string;
}

export const freeTrialApiSlice = createApi({
    reducerPath: 'freeTrialApi',
    baseQuery: fetchBaseQuery({
        baseUrl: getMarketplaceBackend(),
        prepareHeaders: headers => {
            const token = accessToken.getAccessToken();
            if (token) {
                headers.set('Authorization', `Bearer ${token}`);
            }
            return headers;
        },
    }),
    tagTypes: ['FreeTrial'],
    endpoints: builder => ({
        getFreeTrial: builder.query<FreeTrialState, string>({
            query: accountId => `/admin/${accountId}/free-trial`,
            transformResponse: (response: unknown) => mapFreeTrialResponse(response),
            providesTags: (_result, _error, accountId) => [{ type: 'FreeTrial', id: accountId }],
        }),
        updateFreeTrialEligibility: builder.mutation<
            void,
            { accountId: string; status: 'eligible' | 'ineligible'; reason: string }
        >({
            query: ({ accountId, status, reason }) => ({
                url: `/admin/${accountId}/free-trial`,
                method: 'PATCH',
                body: { status, reason } satisfies UpdateFreeTrialEligibilityRequest,
            }),
            invalidatesTags: (_result, _error, { accountId }) => [{ type: 'FreeTrial', id: accountId }],
        }),
        extendFreeTrial: builder.mutation<void, { accountId: string; endsAt: string; reason: string }>({
            query: ({ accountId, endsAt, reason }) => ({
                url: `/admin/${accountId}/free-trial`,
                method: 'PATCH',
                body: { ends_at: endsAt, reason } satisfies ExtendFreeTrialRequest,
            }),
            invalidatesTags: (_result, _error, { accountId }) => [{ type: 'FreeTrial', id: accountId }],
        }),
    }),
});

export const { useGetFreeTrialQuery, useUpdateFreeTrialEligibilityMutation, useExtendFreeTrialMutation } =
    freeTrialApiSlice;
